import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { login as apiLogin, fetchProfile, logout as apiLogout } from "./auth.js";
import { isAuthed, getProfile } from "./session.js";
import { getClientId, loadClientStores as loadStores } from "./stores.js";

const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export function AuthProvider({ children }) {
  // The session is "real" only when a bearer token is stored.
  const [authed, setAuthed] = useState(() => isAuthed());
  const [profile, setProfile] = useState(() => getProfile());
  const [clientStores, setClientStores] = useState(null);
  const [storesLoading, setStoresLoading] = useState(() => isAuthed() && getClientId() != null);

  // client-details FIRST, then headerStores_v2 (sequential, inside loadStores).
  // Non-client users (no clientId) keep null. Failures are non-fatal.
  const loadClientStores = useCallback(async () => {
    setStoresLoading(true);
    try {
      if (getClientId() != null) {
        const stores = await loadStores(); // client-details → then stores
        setClientStores(stores);
      } else {
        setClientStores(null);
      }
    } catch (e) {
      console.warn("Could not load client stores:", e?.message || e);
      setClientStores(null);
    } finally {
      setStoresLoading(false);
    }
  }, []);

  // The full flow: login → profile → (client) stores → authed.
  const login = useCallback(async ({ email, password }) => {
    await apiLogin(email, password);   // stores authenticationToken + refreshToken
    const p = await fetchProfile();    // stores profile, gates the dashboard
    setProfile(p);
    await loadClientStores();          // real client store list, if applicable
    setAuthed(true);
  }, [loadClientStores]);

  const logout = useCallback(() => {
    // Clear the UI immediately; the server logout call runs in the background
    // and clears the local session itself.
    apiLogout();
    setClientStores(null);
    setProfile(null);
    setAuthed(false);
  }, []);

  // On a hard reload with an existing session, re-fetch the client stores so
  // the dashboard isn't stuck empty after a refresh.
  useEffect(() => {
    if (authed) loadClientStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the api client gives up refreshing an expired session, it fires
  // "te-auth-expired" — drop the user back to login.
  useEffect(() => {
    const onExpired = () => { setProfile(null); setClientStores(null); setAuthed(false); };
    window.addEventListener("te-auth-expired", onExpired);
    return () => window.removeEventListener("te-auth-expired", onExpired);
  }, []);

  const value = { authed, profile, clientStores, storesLoading, login, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
